import { AnalyticsService } from 'base/analytics';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { TextRegularLight, TextTinyLight } from 'base/ui/typography';
import React from 'react';

type SitemapImportEntryDialogProps = {
  analytics: AnalyticsService;
  onClose: () => void;
  onStartImport: (opts: { domain: string }) => void;
};

export function SitemapImportEntryDialog({
  analytics,
  onClose,
  onStartImport,
}: SitemapImportEntryDialogProps) {
  const [url, setUrl] = React.useState<string>('');

  const handleUrlOnChange = React.useCallback((url: string) => {
    setUrl(url);
  }, []);
  const handleStartImport = React.useCallback(() => {
    const validatedUrl = validateUrl(url);
    if (validatedUrl.valid) {
      onStartImport({ domain: validatedUrl.domain });
    }
  }, [url, onStartImport]);
  const handleUrlKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // TODO (Luisma): instead of detecting the "Enter" key we should use a form submit event.
      if (e.key === 'Enter') {
        handleStartImport();
      }
    },
    [handleStartImport],
  );
  const handleClose = React.useCallback(() => {
    analytics.track({ event: 'import_sitemap:close_button_click' });
    onClose();
  }, [analytics, onClose]);

  const validatedUrl = validateUrl(url);

  return (
    <Dialog
      title="Import from URL"
      onClose={handleClose}
      buttons={
        <>
          <Button
            background="ai"
            variant="primary"
            label="Import sitemap"
            onClick={handleStartImport}
            size="large"
            disabled={!validatedUrl.valid}
          />
        </>
      }
    >
      <TextRegularLight>
        Enter a website URL, and we'll generate a new project with a matching
        sitemap.
      </TextRegularLight>
      <Rows spacing={8}>
        <FormInput
          autoFocus
          required
          size="large"
          type="text"
          value={url}
          placeholder="Enter website URL"
          invalid={url !== '' && !validatedUrl.valid}
          onChange={handleUrlOnChange}
          onKeyUp={handleUrlKeyUp}
        />
        {validatedUrl.valid && validatedUrl.trailing ? (
          <Inline spacing={4} yalign="center">
            <TextTinyLight color="green">
              {`Import will start from ${validatedUrl.domain}`}
            </TextTinyLight>
          </Inline>
        ) : null}
      </Rows>
    </Dialog>
  );
}

type ValidateUrlResult =
  | {
      valid: true;
      domain: string;
      trailing: boolean;
    }
  | { valid: false };

export function validateUrl(url: string): ValidateUrlResult {
  const m = url.match(
    /^(?:https?:\/\/)?((?:[a-zA-Z0-9\-]+\.)+[a-zA-Z\-]+)(?:\/(.*))?$/,
  );
  if (m === null) {
    return { valid: false };
  }
  return {
    valid: true,
    domain: m[1],
    trailing: !!m[2],
  };
}
